import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import { FaFilePdf } from "react-icons/fa";
import { toast } from "react-toastify";
import { canAccessQtMenus, isLevelOneViewingOtherQuotation } from "../utils/buttonVisibility";
import { emptyPerson, toFormValues, toPayload, validateHandover, remainingPercentage, companyLabel, clientCompanyName, completionMonthOptions, documentDateFromQuotation, mergeLatestQuotationFields } from "../utils/handover";
import { getHandover, getHandoverDefaults, getHandoverClients, saveHandover, getHandoverQuotation, handoverErrorMessage } from "../services/handoverApi";
import HandoverPreview from "./HandoverPreview";
import HandoverPdfDocument from "./HandoverPdfDocument";

const inputClass = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60";
const buttonClass = "rounded-lg px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed";
const COMPANY_BY_EMAIL_DOMAIN = {
  neonworks: "บริษัท นีออน เวิร์คส์ จำกัด",
  optx: "บริษัท ออพท์เอ็กซ์ จำกัด",
};

const currentUserEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user.username || user.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const companyForLoginEmail = (email) => {
  const match = String(email || "").match(/@(neonworks|optx)\./);
  return match ? COMPANY_BY_EMAIL_DOMAIN[match[1]] : "";
};
const isCompanyClient = (client, companyName) => clientCompanyName(client) === companyName;
const quotationClientId = (quotation) => {
  const client = quotation?.clientId;
  return typeof client === "object" ? client?._id || client?.id || "" : client || "";
};
const quotationClientName = (quotation) => typeof quotation?.clientId === "object" ? clientCompanyName(quotation.clientId) : "";

function Field({ id, label, error, children }) {
  return <div className="space-y-1"><label htmlFor={id} className="block text-sm font-medium">{label}</label>{children}{error && <p id={`${id}-error`} className="text-sm text-red-600 dark:text-red-400">{error}</p>}</div>;
}

export default function HandoverSection({ quotation, onQuotationRefresh }) {
  const [open, setOpen] = useState(false);
  const [document, setDocument] = useState(null);
  const [values, setValues] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsReady, setClientsReady] = useState(false);
  const [clientsError, setClientsError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [refreshingSource, setRefreshingSource] = useState(false);
  const [sourceDirty, setSourceDirty] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [forbidden, setForbidden] = useState(false);
  const lifetime = useRef(null);
  const saveLock = useRef(false);
  const loaded = useRef(false);
  const refreshRef = useRef(onQuotationRefresh);
  refreshRef.current = onQuotationRefresh;
  const canceled = quotation.approvalStatus === "Canceled";
  const access = canAccessQtMenus();
  const loginCompanyName = companyForLoginEmail(currentUserEmail());
  const loginCompanyClient = clients.find((client) => isCompanyClient(client, loginCompanyName));
  const quotationClient = quotationClientId(quotation);
  const editable = access && !isLevelOneViewingOtherQuotation(quotation) && !forbidden;
  const disabled = saving || refreshingSource || !editable || canceled || (Boolean(document?._id) && !editing);
  const dirty = values && (JSON.stringify(values) !== baseline || sourceDirty);

  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!dirty || (document?._id && !editing)) return;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, document?._id, editing]);

  useEffect(() => {
    if ((!loginCompanyName || !loginCompanyClient) && !quotationClient) return;
    if (!values) return;
    setValues((current) => {
      const hiringMatches = !loginCompanyClient || (current.hiringClientId === loginCompanyClient._id && current.sender.clientId === loginCompanyClient._id);
      const deliveredMatches = !quotationClient || current.deliveredToClientId === quotationClient;
      const recipientsMatch = !quotationClient || current.recipients.every((person) => person.clientId === quotationClient);
      return hiringMatches && deliveredMatches && recipientsMatch
        ? current
        : {
          ...current,
          ...(loginCompanyClient ? {
            hiringClientId: loginCompanyClient._id,
            sender: { ...current.sender, clientId: loginCompanyClient._id },
          } : {}),
          ...(quotationClient ? { deliveredToClientId: quotationClient } : {}),
          ...(quotationClient ? { recipients: current.recipients.map((person) => ({ ...person, clientId: quotationClient })) } : {}),
        };
    });
  }, [loginCompanyName, loginCompanyClient, quotationClient, values]);

  const hydrate = (data, options = {}) => {
    const form = toFormValues(data, documentDateFromQuotation(quotation));
    setDocument(data); setValues(form); setBaseline(JSON.stringify(form)); setErrors({});
    setEditing(options.editing ?? !Boolean(data?._id));
    setSourceDirty(Boolean(options.sourceDirty));
  };

  const handleError = async (failure, signal) => {
    if (signal.aborted) return;
    setError(handoverErrorMessage(failure));
    if (failure.response?.status === 403) setForbidden(true);
    if (failure.response?.status === 409) {
      try {
        const updated = await getHandoverQuotation(quotation._id, signal);
        if (!signal.aborted) refreshRef.current?.(updated);
      } catch (refreshError) {
        if (!signal.aborted) setError(`${handoverErrorMessage(failure)} — อัปเดตสถานะ QT ไม่สำเร็จ: ${handoverErrorMessage(refreshError)}`);
      }
    }
  };

  const loadClients = async () => {
    const signal = lifetime.current.signal;
    setClientsLoading(true); setClientsError("");
    try {
      const data = await getHandoverClients(signal);
      if (!signal.aborted) { setClients(data); setClientsReady(true); }
    } catch (failure) {
      if (!signal.aborted) setClientsError(handoverErrorMessage(failure));
    } finally { if (!signal.aborted) setClientsLoading(false); }
  };

  const loadDocument = async () => {
    const signal = lifetime.current.signal;
    setLoading(true); setError(""); setForbidden(false);
    try {
      let data;
      try { data = await getHandover(quotation._id, signal); }
      catch (failure) {
        if (signal.aborted) return;
        if (failure.response?.status !== 404 || failure.response?.data?.message !== "Handover not found") throw failure;
        if (!editable) { setError("ยังไม่มีหนังสือส่งมอบงาน และคุณมีสิทธิ์อ่านอย่างเดียว"); return; }
        data = await getHandoverDefaults(quotation._id, signal);
      }
      let sourceChanged = false;
      if (data?._id && editable) {
        try {
          const latest = await getHandoverDefaults(quotation._id, signal);
          const refreshed = mergeLatestQuotationFields(data, latest);
          sourceChanged =
            refreshed.projectName !== data.projectName ||
            refreshed.quotationNumber !== data.quotationNumber;
          data = refreshed;
        } catch (refreshFailure) {
          if (!signal.aborted) {
            setError(`เปิดเอกสารเดิมได้ แต่ดึงข้อมูลล่าสุดจาก QT ไม่สำเร็จ: ${handoverErrorMessage(refreshFailure)}`);
          }
        }
      }
      if (!signal.aborted) hydrate(data, { editing: !Boolean(data?._id), sourceDirty: sourceChanged });
    } catch (failure) { await handleError(failure, signal); }
    finally { if (!signal.aborted) setLoading(false); }
  };

  const toggle = () => {
    setOpen(!open);
    if (!loaded.current && !canceled && access) {
      loaded.current = true;
      loadDocument(); loadClients();
    }
  };
  const update = (key, value) => setValues((current) => ({ ...current, [key]: value }));
  const updatePerson = (key, field, value) => setValues((current) => {
    if (key === "sender") return { ...current, sender: { ...current.sender, [field]: value } };
    const index = Number(key.split(".")[1]);
    return { ...current, recipients: current.recipients.map((person, i) => i === index ? { ...person, [field]: value } : person) };
  });

  const beginEditing = async () => {
    if (!document?._id || !editable || refreshingSource) return;
    const signal = lifetime.current.signal;
    setRefreshingSource(true); setError("");
    try {
      const [latestSources, latestQuotation] = await Promise.all([
        getHandoverDefaults(quotation._id, signal),
        getHandoverQuotation(quotation._id, signal),
      ]);
      if (signal.aborted) return;

      const latestClientId = quotationClientId(latestQuotation);
      const latestClient = typeof latestQuotation?.clientId === "object"
        ? latestQuotation.clientId
        : clients.find((client) => client._id === latestClientId);
      const latestClientSnapshot = latestClientId
        ? {
            clientId: latestClientId,
            companyName:
              clientCompanyName(latestClient) ||
              document.deliveredToClient?.companyName ||
              "",
          }
        : document.deliveredToClient;

      setDocument((current) => ({
        ...mergeLatestQuotationFields(current, latestSources),
        ...(latestClientSnapshot ? { deliveredToClient: latestClientSnapshot } : {}),
      }));
      if (latestClientId) {
        setValues((current) => ({
          ...current,
          deliveredToClientId: latestClientId,
          recipients: current.recipients.map((person) => ({
            ...person,
            clientId: latestClientId,
          })),
        }));
      }
      refreshRef.current?.(latestQuotation);
      setSourceDirty(true);
      setEditing(true);
    } catch (failure) {
      if (!signal.aborted) {
        setEditing(true);
        setError(`เข้าสู่โหมดแก้ไขแล้ว แต่ดึงข้อมูลล่าสุดจาก QT ไม่สำเร็จ: ${handoverErrorMessage(failure)}`);
      }
    } finally {
      if (!signal.aborted) setRefreshingSource(false);
    }
  };

  const cancelEditing = () => {
    if (saving || refreshingSource) return;
    loadDocument();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (disabled || !clientsReady || clientsLoading || clientsError || saveLock.current) return;
    const validation = validateHandover(values, clients);
    setErrors(validation); setError("");
    if (Object.keys(validation).length) { setError("กรุณาตรวจสอบข้อมูลที่ระบุด้านล่าง"); return; }
    saveLock.current = true; setSaving(true);
    const signal = lifetime.current.signal;
    try {
      const data = await saveHandover(quotation._id, toPayload(values), signal);
      if (!signal.aborted) { hydrate(data); toast.success("บันทึกหนังสือส่งมอบงานเรียบร้อยแล้ว"); }
    } catch (failure) { await handleError(failure, signal); }
    finally { saveLock.current = false; if (!signal.aborted) setSaving(false); }
  };

  const downloadPdf = async () => {
    if (!document || !values || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const blob = await pdf(<HandoverPdfDocument document={document} values={values} clients={clients} quotationClientId={quotationClient} />).toBlob();
      const projectName = String(document.projectName || "handover").replace(/[\\/:*?"<>|]/g, "-").trim() || "handover";
      const quotationNumber = String(document.quotationNumber || "handover").replace(/[\\/:*?"<>|]/g, "-").trim() || "handover";
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `หนังสือส่งมอบงาน - ${quotationNumber} - ${projectName}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (failure) {
      console.error("Error generating handover PDF:", failure);
      toast.error("ไม่สามารถสร้าง PDF หนังสือส่งมอบงานได้");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const textField = (key, label, value, onChange, props = {}) => {
    const id = `handover-${key}`;
    return <Field id={id} label={label} error={errors[key]}><input id={id} className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled} aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `${id}-error` : undefined} {...props} /></Field>;
  };
  const completionMonthField = () => {
    const id = "handover-expectedCompletionMonth";
    const monthOptions = completionMonthOptions(new Date(), values.expectedCompletionMonth);
    return <Field id={id} label="เดือนที่คาดว่าจะเสร็จ *" error={errors.expectedCompletionMonth}>
      <select id={id} className={inputClass} value={values.expectedCompletionMonth} onChange={(event) => update("expectedCompletionMonth", event.target.value)} disabled={disabled} aria-invalid={Boolean(errors.expectedCompletionMonth)} aria-describedby={errors.expectedCompletionMonth ? `${id}-error` : undefined}>
        <option value="">เลือกเดือนที่คาดว่าจะเสร็จ</option>
        {monthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <p className="text-xs text-gray-500 dark:text-gray-400">เลือกได้จาก 12 เดือนนับจากเดือนปัจจุบัน</p>
    </Field>;
  };
  const clientField = (key, label, value, snapshot, onChange) => {
    const id = `handover-${key}`;
    const missing = clientsReady && value && !clients.some((client) => client._id === value);
    const issue = errors[key] || (missing ? "บริษัทนี้ไม่อยู่ในรายการแล้ว กรุณาเลือกบริษัทใหม่ก่อนบันทึก" : "");
    return <Field id={id} label={label} error={issue}>
      <Select inputId={id} isSearchable isClearable isDisabled={disabled || clientsLoading || !clientsReady} isLoading={clientsLoading}
        options={clients.map((client) => ({ value: client._id, label: clientCompanyName(client) }))}
        value={value ? { value, label: companyLabel(value, snapshot, clients) } : null}
        onChange={(option) => onChange(option?.value || "")}
        placeholder="ค้นหาและเลือกบริษัท" noOptionsMessage={() => "ไม่พบบริษัท"}
        aria-invalid={Boolean(issue)} aria-describedby={issue ? `${id}-error` : undefined}
        classNames={{ control: () => "!bg-white dark:!bg-gray-900 !border-gray-300 dark:!border-gray-600 !rounded-lg !min-h-[42px]", singleValue: () => "!text-gray-900 dark:!text-gray-100", input: () => "!text-gray-900 dark:!text-gray-100", menu: () => "!bg-white dark:!bg-gray-800 !z-30", option: () => "!text-gray-900 dark:!text-gray-100 hover:!bg-indigo-100 dark:hover:!bg-gray-700" }}
        styles={{ option: (base, state) => ({ ...base, backgroundColor: state.isFocused || state.isSelected ? "rgba(99,102,241,.25)" : "transparent" }) }} />
    </Field>;
  };
  const hiringCompanyField = () => {
    if (!loginCompanyName) return clientField("hiringClientId", "บริษัทผู้ว่าจ้าง *", values.hiringClientId, document.hiringClient, (value) => update("hiringClientId", value));
    return internalCompanyField("hiringClientId", "บริษัทผู้ว่าจ้าง *");
  };
  const internalCompanyField = (key, label) => {
    const issue = errors[key] || (clientsReady && !loginCompanyClient ? `ไม่พบ ${loginCompanyName} ในรายการบริษัท` : "");
    const id = `handover-${key}`;
    return <Field id={id} label={label} error={issue}>
      <input id={id} className={inputClass} value={loginCompanyName} disabled aria-invalid={Boolean(issue)} aria-describedby={issue ? `${id}-error` : undefined} />
    </Field>;
  };
  const deliveredToCompanyField = () => {
    if (!quotationClient) return clientField("deliveredToClientId", "บริษัทที่รับเอกสาร *", values.deliveredToClientId, document.deliveredToClient, (value) => update("deliveredToClientId", value));
    return quotationClientField("deliveredToClientId", "บริษัทที่รับเอกสาร *");
  };
  const quotationClientField = (key, fieldLabel) => {
    const issue = errors[key] || (clientsReady && !clients.some((client) => client._id === quotationClient) ? "บริษัทของ QT ไม่อยู่ในรายการบริษัท" : "");
    const companyName = quotationClientName(quotation) || companyLabel(quotationClient, document.deliveredToClient, clients);
    const id = `handover-${key}`;
    return <Field id={id} label={fieldLabel} error={issue}>
      <input id={id} className={inputClass} value={companyName} disabled aria-invalid={Boolean(issue)} aria-describedby={issue ? `${id}-error` : undefined} />
    </Field>;
  };
  const personFields = (key, title, person, snapshot) => {
    const isRecipient = key.startsWith("recipients.");
    return <fieldset className="space-y-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4"><legend className="px-2 font-semibold text-sm">{title}</legend>
      {textField(`${key}.name`, isRecipient ? "ชื่อ" : "ชื่อ *", person.name, (value) => updatePerson(key, "name", value), { maxLength: 300 })}
      {textField(`${key}.position`, isRecipient ? "ตำแหน่ง" : "ตำแหน่ง *", person.position, (value) => updatePerson(key, "position", value), { maxLength: 300 })}
      {key === "sender" && loginCompanyName ? internalCompanyField(`${key}.clientId`, "บริษัท *") : isRecipient && quotationClient ? quotationClientField(`${key}.clientId`, "บริษัท *") : clientField(`${key}.clientId`, isRecipient ? "บริษัท" : "บริษัท *", person.clientId, snapshot?.company, (value) => updatePerson(key, "clientId", value))}
    </fieldset>;
  };

  return <section className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 sm:p-6 mb-6 border border-gray-100 dark:border-gray-700">
    <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">หนังสือส่งมอบงาน</h2><p className="text-sm text-gray-500 dark:text-gray-400">หนึ่งฉบับต่อใบเสนอราคา · บันทึกเพื่อแก้ไขฉบับเดิม</p></div><div className="flex flex-wrap gap-2"><button type="button" className="rounded-lg px-4 py-2 text-sm font-medium border border-indigo-200 dark:border-indigo-500/50 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 disabled:opacity-50 disabled:cursor-not-allowed" onClick={downloadPdf} disabled={!open || !document || !values || generatingPdf}><FaFilePdf className="inline mr-2" />{generatingPdf ? "กำลังสร้าง PDF…" : "ดาวโหลดหนังสือส่งมอบงาน"}</button>{open && document?._id && editable && (editing ? <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" onClick={cancelEditing} disabled={saving || refreshingSource}>ยกเลิกแก้ไข</button> : <button type="button" className={buttonClass} onClick={beginEditing} disabled={refreshingSource}>{refreshingSource ? "กำลังดึงข้อมูล QT…" : "แก้ไขหนังสือส่งมอบงาน"}</button>)}<button type="button" className={buttonClass} onClick={toggle} disabled={canceled || !access} aria-expanded={open}>{open ? "ย่อหนังสือส่งมอบงาน" : "เปิดหนังสือส่งมอบงาน"}</button></div></div>
    {canceled && <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">ใบเสนอราคานี้ถูกยกเลิก จึงไม่สามารถใช้งานหนังสือส่งมอบงานได้</p>}
    {!access && <p className="mt-3 text-sm">ไม่มีสิทธิ์ใช้งานเมนูใบเสนอราคา</p>}
    {open && <div className="mt-6 space-y-4">
      {!editable && <p className="text-sm text-amber-700 dark:text-amber-300">คุณมีสิทธิ์อ่านอย่างเดียว ไม่สามารถสร้างหรือบันทึกหนังสือได้</p>}
      {loading && <p role="status">กำลังโหลดหนังสือส่งมอบงาน…</p>}
      {error && <div role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3">{error}{!values && !loading && !canceled && <button type="button" className="ml-3 underline" onClick={loadDocument}>ลองโหลดหนังสืออีกครั้ง</button>}</div>}
      {clientsLoading && <p role="status">กำลังโหลดรายชื่อบริษัท…</p>}
      {clientsError && <div role="alert" className="text-red-600 dark:text-red-400">โหลดรายชื่อบริษัทไม่สำเร็จ: {clientsError} <button type="button" className="underline" disabled={clientsLoading} onClick={loadClients}>ลองโหลดบริษัทอีกครั้ง</button></div>}
      {values && document && <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,0.85fr)_minmax(0,1.3fr)] gap-6 items-start">
        <form className="space-y-5 min-w-0" onSubmit={submit} noValidate>
          <div className="rounded-xl bg-gray-50 dark:bg-gray-900 p-4 text-sm space-y-2"><p className="font-semibold">ข้อมูลจากระบบ</p>{[["บริษัทผู้ออกหนังสือ", document.issuerCompany?.name], ["ชื่อแคมเปญ/โครงการ", document.projectName], ["ประเภทงาน", document.workType], ["เลข QT", document.quotationNumber]].map(([label, value]) => <div key={label} className="break-words"><span className="text-gray-500 dark:text-gray-400">{label}: </span>{value}</div>)}</div>
          {textField("documentDate", "วันที่หนังสือ *", values.documentDate, (value) => update("documentDate", value), { type: "date" })}
          {hiringCompanyField()}
          <div className="flex items-end gap-2"><div className="flex-1">{textField("completionPercentage", "เปอร์เซ็นต์งานที่เสร็จ *", values.completionPercentage, (value) => setValues((current) => ({ ...current, completionPercentage: value, expectedCompletionMonth: value !== "" && Number(value) === 100 ? "" : current.expectedCompletionMonth })), { type: "number", min: 0, max: 100, step: "0.01" })}</div><span className="pb-2">%</span></div>
          <p className="text-sm text-indigo-700 dark:text-indigo-300">งานคงเหลือ {String(document.completionPercentage) === values.completionPercentage ? document.remainingPercentage : remainingPercentage(values.completionPercentage) ?? "—"}%</p>
          {values.completionPercentage !== "" && Number(values.completionPercentage) < 100 && completionMonthField()}
          {deliveredToCompanyField()}
          {personFields("sender", "ผู้ส่งมอบงาน", values.sender, document.sender)}
          {personFields("recipients.0", "ผู้รับมอบงานคนที่ 1", values.recipients[0], document.recipients?.[0])}
          <label className="flex gap-2 items-center text-sm"><input type="checkbox" disabled={disabled} checked={values.additionalRecipientEnabled} onChange={(event) => { const checked = event.target.checked; setValues((current) => ({ ...current, additionalRecipientEnabled: checked, recipients: checked ? [current.recipients[0], emptyPerson()] : [current.recipients[0]] })); }} />เพิ่มผู้รับมอบงาน</label>
          {values.additionalRecipientEnabled && personFields("recipients.1", "ผู้รับมอบงานคนที่ 2", values.recipients[1], document.recipients?.[1])}
          <label className="flex gap-2 items-center text-sm"><input type="checkbox" disabled={disabled} checked={values.remarkEnabled} onChange={(event) => { const checked = event.target.checked; setValues((current) => ({ ...current, remarkEnabled: checked, remark: checked ? current.remark : "" })); }} />เพิ่มหมายเหตุ</label>
          {values.remarkEnabled && <Field id="handover-remark" label="หมายเหตุ *" error={errors.remark}><textarea id="handover-remark" className={inputClass} disabled={disabled} rows={5} maxLength={5000} value={values.remark} onChange={(event) => update("remark", event.target.value)} aria-invalid={Boolean(errors.remark)} aria-describedby={errors.remark ? "handover-remark-error" : undefined} /><p className="text-xs text-gray-500">{values.remark.length.toLocaleString()} / 5,000 ตัวอักษร</p></Field>}
          <div className="flex flex-wrap items-center gap-3"><button type="submit" className={buttonClass} disabled={disabled || !clientsReady || clientsLoading || Boolean(clientsError) || (Boolean(document._id) && !dirty)}>{saving ? "กำลังบันทึก…" : "บันทึกหนังสือส่งมอบงาน"}</button><span className="text-sm text-gray-500 dark:text-gray-400">{!editing && document._id && dirty ? "พบข้อมูลใหม่จาก QT · กดแก้ไขเพื่อบันทึก" : dirty ? "มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก" : document._id ? "บันทึกแล้ว" : "ฉบับใหม่"}</span></div>
        </form>
        <HandoverPreview document={document} values={values} clients={clients} quotationClientId={quotationClient} />
      </div>}
    </div>}
  </section>;
}
